import { reactive } from 'vue';
import makeStoreDebugAccessible from '@/core/store/store-debug';
import utilities from '@/shared/utilities.service';
import authStore from '@/shared/auth/auth.store';
import {
    B2CClientService,
    B2CMyBoliaProfileAddress,
    B2CMyBoliaProfileModel, B2CUpdateProfileRequest,
    ChangePasswordRequest, ClientResponse,
} from '@/b2c/shared/api/server-apis';

export enum B2CProfileEditMode {
    Information = 0,
    Password = 1,
    Address = 2,
    Store = 3,
}

class ProfileStore {
    public state: IB2CProfileState = reactive({
        profile: {} as B2CMyBoliaProfileModel,
        editMode: null,
    });

    constructor() {
        makeStoreDebugAccessible('profileStore', this.state);
    }

    async initProfile(): Promise<void> {
        this.state.editMode = null;
        const response = await B2CClientService.profile().then(utilities.unwrapModel);
        this.state.profile = response || {} as B2CMyBoliaProfileModel;
    }

    async updateProfileInformation(request: B2CUpdateProfileRequest, messageId?: string): Promise<void> {
        const response = await B2CClientService.updateProfile(request, messageId).then(utilities.unwrapModel);
        this.state.profile = response || {} as B2CMyBoliaProfileModel;
        await authStore.tryRefreshExpiredAccessToken();
    }

    async updateProfileAddress(address: B2CMyBoliaProfileAddress, messageId?: string): Promise<void> {
        const response = await B2CClientService.updateProfileAddress(address, messageId).then(utilities.unwrapModel);
        this.state.profile.address = response || {} as B2CMyBoliaProfileAddress;
    }

    async updateProfileStore(id: string, messageId?: string): Promise<void> {
        const response = await B2CClientService.updateProfileFavoriteStore({ storeId: id }, messageId).then(utilities.unwrapModel);
        this.state.profile = response || {} as B2CMyBoliaProfileModel;
        await authStore.tryRefreshExpiredAccessToken();
    }

    async deleteProfile(): Promise<ClientResponse> {
        return B2CClientService.deleteProfile();
    }

    async changePassword(request: ChangePasswordRequest, messageId?: string): Promise<void> {
        await B2CClientService.changeUserPassword(request, messageId);
    }

    clearEditMode(): void {
        this.setEditMode(null);
    }

    setEditMode(mode: B2CProfileEditMode | null): void {
        this.state.editMode = mode;
    }

    get profile(): B2CMyBoliaProfileModel {
        return this.state.profile;
    }

    get address(): B2CMyBoliaProfileAddress {
        return this.profile.address || {} as B2CMyBoliaProfileAddress;
    }

    get editMode(): B2CProfileEditMode | null {
        return this.state.editMode;
    }

    get isInformationEditMode(): boolean {
        return this.editMode === B2CProfileEditMode.Information;
    }

    get isPasswordEditMode(): boolean {
        return this.editMode === B2CProfileEditMode.Password;
    }

    get isAddressEditMode(): boolean {
        return this.editMode === B2CProfileEditMode.Address;
    }

    get isStoreEditMode(): boolean {
        return this.editMode === B2CProfileEditMode.Store;
    }
}

export interface IB2CProfileState {
    profile: B2CMyBoliaProfileModel;
    editMode: B2CProfileEditMode | null;
}

export default new ProfileStore();
